import React, { useEffect } from 'react';
import { useFlightSelection } from './hooks/useFlightSelection';
import bookingService from '../../services/bookingService';
import Breadcrumb from './components/Breadcrumb';
import FlightCard from './components/FlightCard';
import FlightFilters from './components/FlightFilters';
import FlightFareSummary from './components/FlightFareSummary';
import OneWayFlightHeader from './components/OneWayFlightHeader';
import ReturnFlightTabs from './components/ReturnFlightTabs';
import MultiCitySegments from './components/MultiCitySegments';
import FlightSummaryTable from './components/FlightSummaryTable';

const FlightSelectionPage = () => {
  const {
    // State
    loading,
    selectedFlights,
    activeFlightTab,
    filters,
    flightType,
    mode,
    totalPassengers,
    searchData,
    searchError,
    multiCitySegments,
    activeSegmentIndex,
    departureAirport,
    arrivalAirport,
    enrichedSegments,

    // Functions
    getDisplayFlights,
    handleFlightSelect,
    handleFilterChange,
    handleResetFilters,
    setActiveFlightTab,
    setActiveSegmentIndex,
    handleNext
  } = useFlightSelection();

  // --- NEW: RECOVERY/REVOCATION LOGIC ---
  useEffect(() => {
    const checkAndRevokeAbandonedLock = async () => {
      const token = localStorage.getItem('pending_booking_token');
      const timestamp = localStorage.getItem('pending_booking_time');

      if (token) {
        const timeDiff = Date.now() - parseInt(timestamp || '0');
        const isRecentlyLocked = timeDiff < 10 * 60 * 1000;

        if (isRecentlyLocked) {
          console.log('🧹 [Frontend] Found abandoned booking token on FlightSelectionPage, revoking:', token);
          try {
            await bookingService.revokeLock(token);
            console.log('✅ [Frontend] Abandoned seat lock revoked successfully');
          } catch (err) {
            console.warn('⚠️ [Frontend] Failed to revoke abandoned lock:', err);
          }
        }

        localStorage.removeItem('pending_booking_token');
        localStorage.removeItem('pending_booking_time');
      }
    };

    checkAndRevokeAbandonedLock();
  }, []);
  // ----------------------------------------

  // Helper functions
  const getPageTitle = () => {
    if (mode === 'details') return 'Flight Details';
    switch (flightType) {
      case 'return': return 'Return Flights';
      case 'multi-city': return 'Multi-City Flights';
      default: return 'One-Way Flights';
    }
  };

  const getNoResultsMessage = () => {
    switch (flightType) {
      case 'return': return 'No return flights available for the selected route and dates.';
      case 'multi-city': return 'No multi-city flights available for the selected routes and dates.';
      default: return 'No flights available for the selected route and date.';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">
              {mode === 'details' ? 'Loading flight details...' : 'Searching for flights...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f5f5] pt-[70px]">
      {/* Breadcrumb Navigation */}
      {mode === 'details' && <Breadcrumb />}

      <main className="max-w-[1200px] mx-auto py-6 px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex gap-6">
          {/* Sidebar - Filters or Fare Summary */}
          {mode === 'details' ? (
            <FlightFareSummary
              selectedFlights={selectedFlights}
              totalPassengers={totalPassengers}
              onNext={handleNext}
            />
          ) : (
            <FlightFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          )}

          {/* Main Content */}
          <div className="flex-1">
            {/* Return flight tabs */}
            {flightType === 'return' && mode === 'search' && (
              <ReturnFlightTabs
                activeTab={activeFlightTab}
                onTabChange={setActiveFlightTab}
                searchData={searchData}
                departureAirport={departureAirport}
                arrivalAirport={arrivalAirport}
              />
            )}

            {/* One-way flight header */}
            {flightType === 'one-way' && mode === 'search' && (
              <OneWayFlightHeader
                from={searchData.from}
                to={searchData.to}
                departure={searchData.departure}
                departureAirport={departureAirport}
                arrivalAirport={arrivalAirport}
              />
            )}

            {/* Multi-city segments */}
            {flightType === 'multi-city' && mode === 'search' && (
              <MultiCitySegments
                segments={multiCitySegments}
                activeSegmentIndex={activeSegmentIndex}
                selectedFlights={selectedFlights}
                onSegmentClick={setActiveSegmentIndex}
                enrichedSegments={enrichedSegments}
              />
            )}

            {/* Flight Summary Table for details mode */}
            {mode === 'details' && (
              <FlightSummaryTable selectedFlights={selectedFlights} />
            )}

            {/* Flight Results for search mode */}
            {mode === 'search' && (
              getDisplayFlights().length > 0 ? (
                <div>
                  {getDisplayFlights().map(flight => (
                    <FlightCard
                      key={flight.id}
                      flight={flight}
                      totalPassengers={totalPassengers}
                      onSelect={handleFlightSelect}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-[#666] text-base">
                    {searchError || getNoResultsMessage()}
                  </div>
                  <div className="text-[#999] text-sm mt-2">
                    Please try different dates or destinations.
                  </div>
                </div>
              )
            )}
          </div>
        </div>
        </div>
      </main>
    </div>
  );
};

export default FlightSelectionPage;